import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from "./locales";

const LOCALE_STORAGE_KEY = "locale";

/**
 * Resolves the active locale by checking (in order):
 * 1. The value stored in localStorage
 * 2. The browser's preferred languages (navigator.languages)
 * 3. The default locale (en-US)
 *
 * Language-only tags (e.g. "en") are matched to the first supported locale
 * with the same language prefix (e.g. "en-US").
 *
 * @param allowedLocales - Optionally restrict to a subset of SUPPORTED_LOCALES
 *   (e.g. parsed from VITE_SUPPORTED_LOCALES). Defaults to all supported locales.
 */
export function resolveLocale(allowedLocales: readonly SupportedLocale[] = SUPPORTED_LOCALES): SupportedLocale {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && (allowedLocales as readonly string[]).includes(stored)) {
    return stored as SupportedLocale;
  }

  for (const browserLocale of navigator.languages) {
    if ((allowedLocales as readonly string[]).includes(browserLocale)) {
      return browserLocale as SupportedLocale;
    }

    const languagePrefix = browserLocale.split("-")[0];
    const match = allowedLocales.find((l) => l.startsWith(`${languagePrefix}-`));
    if (match) return match;
  }

  return allowedLocales.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : allowedLocales[0];
}

/**
 * Persists the chosen locale to localStorage so it survives page reloads.
 */
export function persistLocale(locale: SupportedLocale): void {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}
